import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  Grid,
} from "react-virtualized";
import clsx from "clsx";
import Circle from "@material-ui/icons/FiberManualRecord"; // or the correct path to the Circle component
import {
  alpha,
  Box,
  Collapse,
  Divider,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  amber,
  green,
  lightGreen,
  orange,
  red,
  yellow,
} from "@material-ui/core/colors";
import { getTranslated } from "../../utils/helpers";
import BadgedChipComponent from "../components/badgedChip";
import { CustomDialog } from "../../details/variantDetailGrid";
import { ArrowDropDown, ArrowDropUp } from "@material-ui/icons";
import { BooleanTableCell } from "../../virtualizedTable/tooltipTableCell";
import { isJSONString } from "src/components/utils";
import { useTranslation } from "react-i18next";

export const disableClickListener = (e) => {
  e.stopPropagation();
  e.preventDefault();
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    height: "75vh",
    transition: "all 0.2s",
  },
  rowValues: {
    display: "flex",
    flexDirection: "row",
    height: "100%",
    borderRadius: theme.spacing(2),
    overflow: "hidden",
    gap: theme.spacing(1),
  },
  row: {
    display: "flex",
    flexDirection: "column",
    borderRadius: theme.spacing(1),
    overflow: "hidden",
    height: "100%",
    justifyContent: "center",
    padding: theme.spacing(1),
    border: "1px solid " + theme.palette.primary.main,
  },
  rowRenderer: {
    padding: theme.spacing(1),
    transition: "all 0.2s ease",
  },
  text: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  column: {
    justifyContent: "center",
    display: "flex",
    height: "100%",
    alignItems: "center",
  },
  "red[900]-border": {
    border: `2px solid ${red[900]}`,
    background: `${alpha(red[900], 0.1)}`,
  },
  "red[900]": {
    color: red[900],
    fontWeight: "bold",
  },
  "red[500]-border": {
    border: `2px solid ${red[500]}`,
    background: `${alpha(red[500], 0.1)}`,
  },
  "red[500]": {
    color: red[500],
    fontWeight: "bold",
  },
  "orange[500]-border": {
    border: `2px solid ${orange[500]}`,
    background: `${alpha(orange[500], 0.1)}`,
  },
  "orange[500]": {
    color: orange[500],
    fontWeight: "bold",
  },
  "amber[500]-border": {
    border: `2px solid ${amber[500]}`,
    background: `${alpha(amber[500], 0.1)}`,
  },
  "amber[500]": {
    color: amber[500],
    fontWeight: "bold",
  },
  "yellow[600]-border": {
    border: `2px solid ${yellow[600]}`,
    background: `${alpha(yellow[600], 0.1)}`,
  },
  "yellow[600]": {
    color: yellow[600],
    fontWeight: "bold",
  },
  "lightGreen[500]-border": {
    border: `2px solid ${lightGreen[500]}`,
    background: `${alpha(lightGreen[500], 0.1)}`,
  },
  "lightGreen[500]": {
    color: lightGreen[500],
    fontWeight: "bold",
  },
  "green[500]-border": {
    border: `2px solid ${green[500]}`,
    background: `${alpha(green[500], 0.1)}`,
  },
  "green[500]": {
    color: green[500],
    fontWeight: "bold",
  },
  autocomplete: {},
  autocompleteSplitted: {
    display: "list-item",
    listStyle: "circle",
  },
  pathogenicity: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: theme.spacing(2),
  },
  subGroup: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    width: "100%",
  },
  subGroupRoot: {
    width: "100%",
    flex: "1 0 30%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
  multi_value: {},
  collapse: {
    display: "flex",
    flexDirection: "row",
    height: 250,
    transition: "all 0.2s ease",
    flexWrap: "wrap",
    overflow: "scroll",
  },
  dividerSubGroup: {
    backgroundColor: theme.palette.secondary.main,
  },
  chip: {
    padding: 4,
    borderRadius: 24,
    position: "relative",
  },
  "red[900]-background": {
    backgroundColor: alpha(red[900], 0.1),
  },
  "red[500]-background": {
    backgroundColor: alpha(red[500], 0.1),
  },
  "orange[500]-background": {
    backgroundColor: alpha(orange[500], 0.1),
  },
  "amber[500]-background": {
    backgroundColor: alpha(amber[500], 0.1),
  },
  "yellow[600]-background": {
    backgroundColor: alpha(yellow[600], 0.1),
  },
  "lightGreen[500]-background": {
    backgroundColor: alpha(lightGreen[500], 0.1),
  },
  "green[500]-background": {
    backgroundColor: alpha(green[500], 0.1),
  },
  badged: {
    display: "flex",
    width: "100%",
  },
  chipBox: {
    display: "flex",
    gap: theme.spacing(1),
  },
  subGroupLabel: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(1),
    "& p": {
      fontSize: 12,
    },
  },
  box: {
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    maxWidth: 250,
  },
  innerBox: {
    display: "flex",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(1),
    flexDirection: "row",
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  boxHeader: {
    "& p": {
      fontSize: 12,
      fontWeight: "bold",
      color: theme.palette.primary.main,
    },
  },
  phenotypeText: {
    fontSize: "14px",
    fontWeight: 400,
    color: theme.palette.text.primary,
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    "& > *": {
      fontSize: "14px",
      fontWeight: 400,
    },
  },
}));

const ColumnCell = ({ schema, value, hpoFilters }) => {
  const classes = useStyles();
  return (
    schema && (
      <Box className={classes.box}>
        <Box className={classes.boxHeader}>
          {schema.label && (
            <Typography>{getTranslated(schema.label)}</Typography>
          )}
        </Box>
        <Box className={classes.innerBox}>
          {schema.representation.options.massive_insight.component ===
            "pathogenicity" && (
            <Box
              className={clsx(
                classes[
                  `${schema.representation.options.style[value].color}-border`
                ],
                classes.pathogenicity,
                classes.column
              )}
            >
              <Circle
                fontSize="large"
                className={
                  classes[schema.representation.options.style[value].color]
                }
              />
              <Typography
                className={
                  classes[schema.representation.options.style[value].color]
                }
              >
                {schema.representation.options.component_choices[value]}
              </Typography>
            </Box>
          )}
          {schema.representation.options.massive_insight.component ===
            "text" && (
            <Tooltip title={value}>
              <Box className={classes.text}>{value}</Box>
            </Tooltip>
          )}
          {schema.representation.options.massive_insight.component ===
            "badged-chip" && (
            <Box>
              <BadgedChipComponent
                value={isJSONString(value) ? JSON.parse(value) : value}
                classes={classes}
              />
            </Box>
          )}
          {schema.representation.options.massive_insight.component ===
            "dialog" && (
            <>
              <CustomDialog
                data={{
                  value: value,
                  label: getTranslated(schema.label),
                  multi_value: schema.multi_value,
                }}
              />
            </>
          )}
          {schema.representation.options.massive_insight.component ===
            "collapse" && <></>}
          {schema.representation.options.massive_insight.component ===
            "boolean" && (
            <>
              <BooleanTableCell data={value} />
            </>
          )}
          {schema.representation.options.massive_insight.component ===
            "json" && (
            <>
              {isJSONString(value) ? (
                <Box className={classes.phenotypeText}>
                  {Object.entries(JSON.parse(value)).map(
                    ([key, val], index) => (
                      <Typography component="span" key={index}>
                        <i>{key}</i>: {val}
                        {index < Object.entries(JSON.parse(value)).length - 1
                          ? ","
                          : ""}
                        &nbsp;
                      </Typography>
                    )
                  )}
                </Box>
              ) : (
                <Typography className={classes.phenotypeText}>
                  {value}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    )
  );
};

const CollapseColumn = ({ open, data }) => {
  return (
    <Collapse in={open}>
      <Box>
        {data.value &&
          data.value.split(data.multi_value).map((value) => {
            return <Box>{value}</Box>;
          })}
      </Box>
    </Collapse>
  );
};

const SubGroup = ({ schema, value }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);

  return (
    <Box className={classes.subGroup}>
      <Box className={classes.subGroupLabel}>
        {schema.representation.options.massive_insight.component ===
          "collapse" && (
          <IconButton size="small" onClick={() => setOpen((prev) => !prev)}>
            <ArrowDropDown
              color="secondary"
              style={{ transform: open ? "rotate(180deg)" : "rotate(0)" }}
            />
          </IconButton>
        )}
        <Typography noWrap className={classes.subGroupLabel}>
          <b>{getTranslated(schema.label)}</b>&nbsp;:&nbsp;
        </Typography>
      </Box>
      {schema.representation.options.massive_insight.component === "text" && (
        <>
          <Typography variant="subtitle2">{value}</Typography>
        </>
      )}
      {schema.representation.options.massive_insight.component ===
        "badged-chip" && (
        <>
          <BadgedChipComponent
            value={isJSONString(value) ? JSON.parse(value) : value}
            classes={classes}
          />
        </>
      )}
      {schema.representation.options.massive_insight.component === "dialog" && (
        <Box>
          <CustomDialog
            data={{
              value: value,
              label: getTranslated(schema.label),
              multi_value: schema.multi_value,
            }}
          />
        </Box>
      )}
      {schema.representation.options.massive_insight.component ===
        "collapse" && (
        <>
          <CollapseColumn
            open={open}
            data={{
              value: value,
              label: getTranslated(schema.label),
              multi_value: schema.multi_value,
            }}
          />
        </>
      )}
      {schema.representation.options.massive_insight.component ===
        "boolean" && (
        <>
          <BooleanTableCell data={value} />
        </>
      )}
      {schema.representation.options.massive_insight.component === "json" && (
        <>
          {isJSONString(value) ? (
            <Box className={classes.phenotypeText}>
              {Object.entries(JSON.parse(value)).map(([key, val], index) => (
                <Typography component="span" key={index}>
                  <i>{key}</i> : {val}
                  {index < Object.entries(JSON.parse(value)).length - 1
                    ? ","
                    : ""}
                  &nbsp;
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography className={classes.phenotypeText}>{value}</Typography>
          )}
        </>
      )}
    </Box>
  );
};

const SubGroups = ({ subGroupSchema, data, index }) => {
  const classes = useStyles();
  const [groupedColumns, setGroupedColumns] = useState();
  const [subGroupConfig, setSubGroupConfig] = useState();
  const { t } = useTranslation();

  useEffect(() => {
    // Find how many column has and grouping columns with title
    let groupedColumns = {};
    let columns = {};

    for (const key in subGroupSchema) {
      const schema = subGroupSchema[key];
      if (
        !columns[key] &&
        schema.representation.options.massive_insight.sub_group.title
      ) {
        columns = {
          ...columns,
          [schema.representation.options.massive_insight.sub_group.column]: {
            ...schema.representation.options.massive_insight.sub_group,
          },
        };
      }

      if (
        schema.representation.options.massive_insight.sub_group.column ||
        schema.representation.options.massive_insight.sub_group.column === 0
      ) {
        groupedColumns = {
          ...groupedColumns,
          [schema.representation.options.massive_insight.sub_group.column]: [
            ...(groupedColumns[
              schema.representation.options.massive_insight.sub_group.column
            ]
              ? groupedColumns[
                  schema.representation.options.massive_insight.sub_group.column
                ]
              : []),
            schema,
          ],
        };
      }
    }

    setSubGroupConfig(columns);
    setGroupedColumns(groupedColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className={classes.collapse} onClick={disableClickListener}>
      {groupedColumns &&
        subGroupConfig &&
        Object.keys(groupedColumns).map((key) => {
          const columnName = key;
          const columnSchemas = groupedColumns[key];

          return (
            <Box className={classes.subGroupRoot}>
              <Box>
                <Typography
                  variant="56"
                  color="secondary"
                  className={classes.sub}
                >
                  {!subGroupConfig[columnName].hide_title ? (
                    <b>{t(subGroupConfig[columnName].title)}</b>
                  ) : (
                    <>&nbsp;</>
                  )}
                </Typography>
                <Divider className={classes.dividerSubGroup} />
              </Box>
              <Box display="flex" flexDirection="column">
                {columnSchemas.map((schema) => (
                  <SubGroup schema={schema} value={data[index][schema.name]} />
                ))}
              </Box>
            </Box>
          );
        })}
    </Box>
  );
};

const RowRenderer = ({
  key,
  rowIndex,
  style,
  data,
  schemas,
  parent,
  subGroupSchema,
  handleClickRow,
  cache,
  hpoFilters,
  isExpended,
}) => {
  const classes = useStyles();
  return (
    <CellMeasurer
      cache={cache}
      columnIndex={0}
      key={key}
      rowIndex={rowIndex}
      parent={parent}
    >
      <div key={key} style={style} className={classes.rowRenderer}>
        <Box
          className={classes.row}
          onClick={() => {
            handleClickRow(rowIndex);
          }}
        >
          <Box className={classes.rowValues}>
            <Box display="flex" alignItems="center">
              <IconButton size="small">
                {isExpended ? (
                  <ArrowDropUp color="secondary" />
                ) : (
                  <ArrowDropDown color="secondary" />
                )}
              </IconButton>
            </Box>
            {Object.keys(schemas).map(
              (key) =>
                !schemas[
                  key
                ].representation.options.massive_insight.hasOwnProperty(
                  "sub_group"
                ) && (
                  <ColumnCell
                    schema={schemas[key]}
                    value={data[rowIndex][key]}
                    hpoFilters={hpoFilters}
                  />
                )
            )}
          </Box>
          <Box>
            <Collapse in={isExpended} timeout={0}>
              <SubGroups
                subGroupSchema={subGroupSchema}
                index={rowIndex}
                data={data}
              />
            </Collapse>
          </Box>
        </Box>
      </div>
    </CellMeasurer>
  );
};

function InsightVariants({ schemas, hpoFilters, insightData }) {
  const ref = useRef();
  const classes = useStyles();
  const [subGroupSchema, setSubGroupSchema] = useState();
  const [isExpended, setIsExpanded] = useState(
    new Array(insightData.length).fill(150)
  );
  const cache = useMemo(() => {
    return new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 100,
    });
  }, []);

  useEffect(() => {
    if (schemas) {
      let tmpSubGroupSchema = {};
      Object.keys(schemas).forEach((key) => {
        const schema = schemas[key];
        if (
          schema.representation.options.hasOwnProperty("massive_insight") &&
          schema.representation.options.massive_insight.hasOwnProperty(
            "sub_group"
          )
        ) {
          tmpSubGroupSchema = {
            ...tmpSubGroupSchema,
            [schema.name]: schema,
          };
        }
      });

      tmpSubGroupSchema = Object.entries(tmpSubGroupSchema)
        .sort(([, a], [, b]) => {
          const aOrder =
            a.representation.options.massive_insight.sub_group.order ?? 0;
          const bOrder =
            b.representation.options.massive_insight.sub_group.order ?? 0;
          return aOrder - bOrder;
        })
        .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});

      setSubGroupSchema(tmpSubGroupSchema);
    }
  }, [schemas]);

  const handleClickRow = (index) => {
    if (isExpended[index] === 150) {
      isExpended[index] = 400;
    } else {
      isExpended[index] = 150;
    }

    setIsExpanded([...isExpended]);
    cache.clear(index, 0);
    if (ref.current) {
      ref.current.recomputeGridSize();
    }
  };

  const calculateRowHeight = ({ index }) => {
    return isExpended[index];
  };

  return (
    subGroupSchema &&
    insightData.length > 0 && (
      <Box className={classes.root}>
        <AutoSizer>
          {({ width, height }) => (
            <Grid
              ref={ref}
              rowHeight={calculateRowHeight}
              columnCount={1}
              overscanRowCount={14}
              columnWidth={width}
              width={width}
              className={classes.list}
              deferredMeasurementCache={cache}
              height={height}
              rowCount={insightData.length}
              cellRenderer={(props) => (
                <RowRenderer
                  {...props}
                  data={insightData}
                  schemas={schemas}
                  subGroupSchema={subGroupSchema}
                  handleClickRow={handleClickRow}
                  cache={cache}
                  hpoFilters={hpoFilters}
                  isExpended={isExpended[props.rowIndex] !== 150}
                />
              )}
            />
          )}
        </AutoSizer>
      </Box>
    )
  );
}

export default InsightVariants;
